import type { ReactNode } from 'react'
import { LogOut, UserPen } from 'lucide-react'
import { Avatar, IconButton, Menu, MenuGroup, MenuItem, MenuLinkItem, MenuSeparator } from '@/components/play'
import { mayLeave, useLeaveGuard, type BeforeLeave } from '@/components/siteLeave'
import { displayNameFor, personToneKey } from '@/lib/profile'
import { useAuth } from '@/state/AuthContext'

/** The page Edit profile comes back to: an app path, such as the watch screen's, and that page's name, such as the
 *  event's. */
export type ProfileReturn = { to: string; label?: string }

/** The account page, told where its Back leads (`return_to`) and what to call it (`return_label`). */
function profileLink(back?: ProfileReturn) {
  if (!back) return '/settings'
  const params = new URLSearchParams({ return_to: back.to })
  if (back.label) params.set('return_label', back.label)
  return `/settings?${params}`
}

type AccountMenuProps = {
  /** Items under Edit profile, such as development tools. */
  children?: ReactNode
  /** Asked before Edit profile, a link among `children` or Sign out takes the person off the page; return false to
   *  hold it (SiteHeader's `beforeLeave`). The menu closes either way. */
  beforeLeave?: BeforeLeave
  /** Edit profile comes back here: the account page's Back leads to `to` and says `label` (or just Back without one).
   *  Without it, Back leads to the person's events, or home for a viewer. */
  profileReturn?: ProfileReturn
}

/** The signed-in person's photo (or initials, in their tone: the same as on the watch screen), top right on every
 *  signed-in page: their name, Edit profile and Sign out. `children` adds items under Edit profile, such as
 *  development tools. */
export function AccountMenu({ children, beforeLeave, profileReturn }: AccountMenuProps) {
  const { user, logout } = useAuth()
  const follow = useLeaveGuard(beforeLeave)
  const name = displayNameFor(user)
  const signOut = () => {
    if (mayLeave(beforeLeave, { kind: 'sign_out' }, logout)) logout()
  }
  return (
    <Menu
      align="end"
      trigger={
        <IconButton label="Your account" variant="ghost">
          <Avatar
            name={name}
            src={user?.attributes.avatar_url}
            size="compact"
            tone="auto"
            toneKey={user ? personToneKey({ handle: user.attributes.username, id: user.id }) : null}
          />
        </IconButton>
      }
      onNavigate={(event, link) => follow(event, link.to, { replace: link.replace })}
    >
      <MenuGroup label={name || 'Your account'}>
        <MenuLinkItem to={profileLink(profileReturn)} icon={<UserPen />}>Edit profile</MenuLinkItem>
        {children}
      </MenuGroup>
      <MenuSeparator />
      <MenuItem icon={<LogOut />} onClick={signOut}>Sign out</MenuItem>
    </Menu>
  )
}
